import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Typography } from "@mui/material";
import { view360StyleProps } from "../theme/main";

export interface Config360 {
  frameCount: number;
  frameStep?: number;
  basePath?: string; // e.g., "/car/base_{frame}.png"
  options: Record<string, Record<string, string>>; // e.g., { paint: { Red: "/car/red_{frame}.png" } }
  selectedOptions?: Record<string, string>;
}

export type View360Handle = {
  setFrame: (frame: number) => void;
  resetFrame: () => void;
};

type View360Props = {
  config: Config360;
};

type SequenceEntry = {
  optionType: string;
  optionName: string;
  cacheKey: string;
  pathPattern: string;
};

type ImageLoadJob = {
  cacheKey: string;
  pathPattern: string;
  frameIndex: number;
  optionType: string;
  optionName: string;
};

const imageLoadBatchSize = 5;
const maxCachedSequences = 12;
const sparseFrameInterval = 4;

function decodeImage(img: HTMLImageElement) {
  if (img.decode) {
    return img.decode();
  }

  return new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () =>
      reject(new Error("The source image cannot be decoded."));
  });
}

function getDefaultSelections(options: Config360["options"]) {
  return Object.entries(options).reduce<Record<string, string>>(
    (selections, [optionType, optionValues]) => {
      const optionNames = Object.keys(optionValues);
      const defaultOption =
        optionNames.find((optionName) => optionName === "Default") ||
        optionNames.find((optionName) => optionName === "None") ||
        optionNames[0];

      if (defaultOption) {
        selections[optionType] = defaultOption;
      }

      return selections;
    },
    {},
  );
}

function getOptionCacheKey(optionType: string, optionName: string) {
  return `${optionType}:${optionName}`;
}

function getSequenceCacheKey(
  optionType: string,
  optionName: string,
  pathPattern: string,
) {
  return `${getOptionCacheKey(optionType, optionName)}:${pathPattern}`;
}

function formatFrameNumber(frameIndex: number, frameStep = 1) {
  return (frameIndex * frameStep).toString().padStart(4, "0");
}

function getUniqueFrameIndexes(frameIndexes: number[]) {
  return frameIndexes.filter(
    (frameIndex, index) =>
      frameIndex >= 0 && frameIndexes.indexOf(frameIndex) === index,
  );
}

function getSparseFrameIndexes(frameCount: number) {
  return Array.from(
    { length: frameCount },
    (_, frameIndex) => frameIndex,
  ).filter((frameIndex) => frameIndex % sparseFrameInterval === 0);
}

function getFramesAroundCurrent(currentFrame: number, frameCount: number) {
  const frameIndexes = [currentFrame];

  for (let offset = 1; offset < frameCount; offset++) {
    frameIndexes.push((currentFrame + offset) % frameCount);
    frameIndexes.push((currentFrame - offset + frameCount) % frameCount);
  }

  return getUniqueFrameIndexes(frameIndexes);
}

function getRemainingFrameIndexes(frameCount: number, loadedFirst: number[]) {
  return Array.from(
    { length: frameCount },
    (_, frameIndex) => frameIndex,
  ).filter((frameIndex) => !loadedFirst.includes(frameIndex));
}

function getExactFrame(
  frames: HTMLImageElement[] | undefined,
  currentFrame: number,
) {
  return frames?.[currentFrame];
}

const View360 = forwardRef<View360Handle, View360Props>(function View360(
  { config },
  ref,
) {
  const [currentFrame, setCurrentFrame] = useState(0);
  const [isPreloading, setIsPreloading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [hasRendered, setHasRendered] = useState(false);
  const [cacheVersion, setCacheVersion] = useState(0);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageCache = useRef<Map<string, HTMLImageElement[]>>(new Map());
  const cacheAccess = useRef<Map<string, number>>(new Map());
  const loadingFrames = useRef<Map<string, Promise<boolean>>>(new Map());
  const failedFrames = useRef<Set<string>>(new Set());
  const loadRunId = useRef(0);
  const isDragging = useRef(false);
  const startX = useRef(0);

  useImperativeHandle(
    ref,
    () => ({
      setFrame: (frame) => {
        setCurrentFrame(
          ((frame % config.frameCount) + config.frameCount) % config.frameCount,
        );
      },
      resetFrame: () => setCurrentFrame(0),
    }),
    [config.frameCount],
  );

  const firstOptionPath = Object.values(config.options)
    .flatMap((optionValues) => Object.values(optionValues))
    .find(Boolean);
  const frameStep = config.frameStep || 1;
  const previewPath = config.basePath || firstOptionPath || "";
  const firstFrameSrc = previewPath.replace("{frame}", "0000");
  const {
    preloaderRootStyle,
    preloaderPreviewImageStyle,
    preloaderOverlayStyle,
    preloaderProgressStyle,
    rootStyle,
    canvasStyle,
    canvasGrabCursor,
    canvasGrabbingCursor,
  } = view360StyleProps;
  const selections = useMemo(
    () => ({
      ...getDefaultSelections(config.options),
      ...config.selectedOptions,
    }),
    [config.options, config.selectedOptions],
  );
  const optionEntries = useMemo<SequenceEntry[]>(
    () =>
      Object.entries(config.options).flatMap(([optionType, optionValues]) =>
        Object.entries(optionValues).map(([optionName, pathPattern]) => ({
          optionType,
          optionName,
          cacheKey: getSequenceCacheKey(optionType, optionName, pathPattern),
          pathPattern,
        })),
      ),
    [config.options],
  );
  const selectedEntries = useMemo(
    () =>
      optionEntries.filter(
        (entry) => selections[entry.optionType] === entry.optionName,
      ),
    [optionEntries, selections],
  );

  // 1. PRELOADER
  useEffect(() => {
    const runId = loadRunId.current + 1;
    loadRunId.current = runId;

    const baseEntry = config.basePath
      ? {
          optionType: "base",
          optionName: "base",
          cacheKey: getSequenceCacheKey("base", "base", config.basePath),
          pathPattern: config.basePath,
        }
      : undefined;
    const selectedSequences = [
      ...(baseEntry ? [baseEntry] : []),
      ...selectedEntries,
    ];
    const selectedCacheKeys = selectedSequences.map((entry) => entry.cacheKey);
    const nearbyFrameIndexes = getFramesAroundCurrent(
      currentFrame,
      config.frameCount,
    );
    const firstPassFrameIndexes = nearbyFrameIndexes.slice(
      0,
      imageLoadBatchSize,
    );
    const sparseFrameIndexes = getUniqueFrameIndexes([
      ...firstPassFrameIndexes,
      ...getSparseFrameIndexes(config.frameCount).sort(
        (a, b) => nearbyFrameIndexes.indexOf(a) - nearbyFrameIndexes.indexOf(b),
      ),
    ]);
    const remainingFrameIndexes = getRemainingFrameIndexes(
      config.frameCount,
      sparseFrameIndexes,
    );
    const selectedFrameTotal =
      selectedSequences.length * config.frameCount || 1;
    let selectedFramesLoaded = 0;

    const touchSequence = (cacheKey: string) => {
      cacheAccess.current.set(cacheKey, Date.now());
    };

    const trimCache = () => {
      const protectedCacheKeys = new Set(selectedCacheKeys);

      if (imageCache.current.size <= maxCachedSequences) {
        return;
      }

      const removableEntries = Array.from(imageCache.current.keys())
        .filter((cacheKey) => !protectedCacheKeys.has(cacheKey))
        .sort(
          (a, b) =>
            (cacheAccess.current.get(a) || 0) -
            (cacheAccess.current.get(b) || 0),
        );

      while (
        imageCache.current.size > maxCachedSequences &&
        removableEntries.length > 0
      ) {
        const cacheKey = removableEntries.shift();

        if (cacheKey) {
          imageCache.current.delete(cacheKey);
          cacheAccess.current.delete(cacheKey);
        }
      }
    };

    const getJobs = (entries: SequenceEntry[], frameIndexes: number[]) =>
      entries.flatMap((entry) =>
        frameIndexes
          .filter(
            (frameIndex) =>
              !imageCache.current.get(entry.cacheKey)?.[frameIndex],
          )
          .map((frameIndex) => ({
            cacheKey: entry.cacheKey,
            pathPattern: entry.pathPattern,
            frameIndex,
            optionType: entry.optionType,
            optionName: entry.optionName,
          })),
      );

    const loadFrame = (job: ImageLoadJob) => {
      if (imageCache.current.get(job.cacheKey)?.[job.frameIndex]) {
        touchSequence(job.cacheKey);
        return Promise.resolve(true);
      }

      const frameCacheKey = `${job.cacheKey}:${job.frameIndex}`;
      const existingLoad = loadingFrames.current.get(frameCacheKey);

      if (existingLoad) {
        return existingLoad;
      }

      const load = (async () => {
        const img = new Image();
        const src = job.pathPattern.replace(
          "{frame}",
          formatFrameNumber(job.frameIndex, frameStep),
        );

        try {
          img.src = src;
          await decodeImage(img);
        } catch (error) {
          console.warn(
            `Unable to decode 360 frame for ${job.optionType} / ${job.optionName}: ${src}`,
            error,
          );
          failedFrames.current.add(frameCacheKey);
          return false;
        }

        if (loadRunId.current !== runId) {
          return false;
        }

        const frames = imageCache.current.get(job.cacheKey) || [];
        frames[job.frameIndex] = img;
        imageCache.current.set(job.cacheKey, frames);
        failedFrames.current.delete(frameCacheKey);
        touchSequence(job.cacheKey);
        trimCache();
        return true;
      })();

      loadingFrames.current.set(frameCacheKey, load);
      load.finally(() => loadingFrames.current.delete(frameCacheKey));

      return load;
    };

    const loadJobs = async (
      jobs: ImageLoadJob[],
      onBatchLoaded?: (loadedCount: number) => void,
    ) => {
      for (
        let batchStart = 0;
        batchStart < jobs.length;
        batchStart += imageLoadBatchSize
      ) {
        if (loadRunId.current !== runId) {
          return;
        }

        const batch = jobs.slice(batchStart, batchStart + imageLoadBatchSize);
        await Promise.all(batch.map(loadFrame));
        onBatchLoaded?.(batch.length);
        setCacheVersion((version) => version + 1);
      }
    };

    const updateSelectedProgress = (loadedCount: number) => {
      selectedFramesLoaded += loadedCount;
      setProgress(
        Math.min(
          100,
          Math.round((selectedFramesLoaded / selectedFrameTotal) * 100),
        ),
      );
    };

    const init = async () => {
      setIsPreloading(!hasRendered);
      setProgress(0);

      await loadJobs(
        getJobs(selectedSequences, firstPassFrameIndexes),
        updateSelectedProgress,
      );

      if (loadRunId.current !== runId) {
        return;
      }

      setIsPreloading(false);
      setHasRendered(true);
      setCacheVersion((version) => version + 1);

      await loadJobs(
        getJobs(selectedSequences, sparseFrameIndexes),
        updateSelectedProgress,
      );
      await loadJobs(
        getJobs(selectedSequences, remainingFrameIndexes),
        updateSelectedProgress,
      );

      const backgroundEntries = optionEntries.filter(
        (entry) => !selectedCacheKeys.includes(entry.cacheKey),
      );

      await loadJobs(getJobs(backgroundEntries, [currentFrame]));
    };

    init();
  }, [
    config.basePath,
    config.frameCount,
    currentFrame,
    frameStep,
    optionEntries,
    selectedEntries,
  ]);

  // 2. RENDER LOOP (The "Professional" Draw)
  const drawFrame = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d"); // alpha: false for performance
    if (!canvas || !ctx) return;

    const selectedFrames = selectedEntries.map((entry) => ({
      frame: getExactFrame(
        imageCache.current.get(entry.cacheKey),
        currentFrame,
      ),
      failed: failedFrames.current.has(`${entry.cacheKey}:${currentFrame}`),
    }));
    const baseFrames = config.basePath
      ? imageCache.current.get(
          getSequenceCacheKey("base", "base", config.basePath),
        )
      : undefined;
    const baseFrame = config.basePath
      ? getExactFrame(baseFrames, currentFrame)
      : undefined;
    const baseFrameFailed =
      config.basePath &&
      failedFrames.current.has(
        `${getSequenceCacheKey("base", "base", config.basePath)}:${currentFrame}`,
      );
    const drawableSelectedFrames = selectedFrames
      .map(({ frame }) => frame)
      .filter((frame): frame is HTMLImageElement => Boolean(frame));
    const sizingImage = baseFrame || drawableSelectedFrames[0];

    if (
      selectedFrames.some(({ frame, failed }) => !frame && !failed) ||
      (config.basePath && !baseFrame && !baseFrameFailed)
    ) {
      return;
    }

    if (!sizingImage) return;

    // Set canvas size once
    if (canvas.width !== sizingImage.width) {
      canvas.width = sizingImage.width;
      canvas.height = sizingImage.height;
    }

    // Clear and Draw Layers
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (baseFrame) {
      ctx.drawImage(baseFrame, 0, 0);
    }

    for (const optionFrame of drawableSelectedFrames) {
      if (optionFrame) {
        ctx.drawImage(optionFrame, 0, 0);
      }
    }
  }, [cacheVersion, config.basePath, currentFrame, selectedEntries]);

  useEffect(() => {
    if (!isPreloading || hasRendered) drawFrame();
  }, [hasRendered, isPreloading, drawFrame]);

  // 3. INTERACTION LOGIC
  const handlePointerDown = (e: React.PointerEvent) => {
    isDragging.current = true;
    startX.current = e.clientX;
    (e.target as Element).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging.current) return;
    const diff = e.clientX - startX.current;

    // Sensitivity: how many pixels moved = 1 frame change
    const sensitivity = 15;
    if (Math.abs(diff) > sensitivity) {
      const frameDelta = diff > 0 ? 1 : -1;
      setCurrentFrame(
        (prev) => (prev + frameDelta + config.frameCount) % config.frameCount,
      );
      startX.current = e.clientX;
    }
  };

  const handlePointerUp = () => {
    isDragging.current = false;
  };

  if (isPreloading) {
    return (
      <Box
        sx={{
          ...preloaderRootStyle,
          width: "100vw",
          height: "100vh",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
        }}
      >
        {firstFrameSrc && (
          <Box
            component="img"
            src={firstFrameSrc}
            alt=""
            sx={{
              ...preloaderPreviewImageStyle,
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
            }}
          />
        )}
        <Box
          sx={{
            ...preloaderOverlayStyle,
            position: "absolute",
            inset: 0,
          }}
        />
        <Box sx={{ position: "relative", zIndex: 1, marginRight: "20%" }}>
          <Typography sx={preloaderProgressStyle}>{progress + "%"}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...rootStyle,
        width: "80vw",
        height: "100vh",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        component="canvas"
        ref={canvasRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        sx={{
          ...canvasStyle,
          maxWidth: "100%",
          // maxHeight: "100%",
          width: "auto",
          height: "auto",
          // boxShadow: "0 0 7px 7px #cdd0d1, inset 0 0 7px 7px #cdd0d1",
          // border: "6px solid #cdd0d1",

          boxSizing: "border-box",
          cursor: isDragging.current ? canvasGrabbingCursor : canvasGrabCursor,
        }}
      />
    </Box>
  );
});

export default View360;
